import React, { type PropsWithChildren, useCallback } from "react"
import styled, { useTheme } from "styled-components"
import { Icon, type IconProps } from "../Icon"
import type { CSSTextTransform } from "../types/css"
import type { HTMLButtonType } from "../types/html"
import type { BaseProps } from "../types/props"
import { contrastColor, overloadColor } from "../util/style"
import { isFunction, isUndefined } from "../util/typechecks"

type ButtonWrapperProps = {
  disabled: boolean

  width: string
  height: string
  borderRadius: string

  color: string
  background: string
  fontSize: string
}
const ButtonWrapper = styled.button<ButtonWrapperProps>`
  display: flex;
  align-items: center;

  width: ${(props) => props.width};
  height: ${(props) => props.height};
  padding: 0;

  border: none;
  border-radius: ${(props) => props.borderRadius};

  cursor: pointer;
  user-select: none;
  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};
  font-size: ${(props) => props.fontSize};

  &:focus {
    outline: none;
    filter: brightness(1.1);
  }
  &:active {
    filter: brightness(1.2);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;

    &:focus,
    &:active {
      filter: none;
    }
  }
`
interface ContentProps {
  textTransform: string
  padding: string
  center: boolean
}
const Content = styled.div<ContentProps>`
  display: flex;
  justify-content: ${(props) => (props.center ? "center" : "flex-start")};
  align-items: center;
  width: 100%;

  text-transform: ${(props) => props.textTransform};
  padding: ${(props) => props.padding};
`
const ICON_WIDTH = 42
interface IconWrapperProps {
  position: "left" | "right"
  background: string
  height: string
  buttonBackground: string
  borderRadius: string
}
const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(props) => props.height};
  min-width: ${ICON_WIDTH}px;
  width: ${ICON_WIDTH}px;
  max-width: ${ICON_WIDTH}px;
  background: ${(props) => overloadColor(props.background)};

  border: none;
  border-radius: ${(props) =>
    props.position === "left"
      ? `${props.borderRadius} 0 0 ${props.borderRadius}`
      : `0 ${props.borderRadius} ${props.borderRadius} 0`};
  border-width: ${(props) =>
    props.position === "left" ? "1px 0 1px 1px" : "1px 1px 1px 0"};

  color: ${(props) =>
    contrastColor(
      overloadColor(props.buttonBackground),
      "#dde",
      "#557",
      "#dde",
    )};
`

export interface ButtonIconProps extends IconProps {
  background?: string
}
export interface ButtonProps extends BaseProps, PropsWithChildren<object> {
  // Functional

  disabled?: boolean
  tabIndex?: number

  type?: HTMLButtonType
  iconLeftProps?: ButtonIconProps
  iconRightProps?: ButtonIconProps

  autoFocus?: boolean

  // Style

  width?: string
  fullWidth?: boolean
  height?: string
  padding?: string
  borderRadius?: string

  color?: string
  background?: string
  fontSize?: string
  textTransform?: CSSTextTransform
  centerContent?: boolean

  // Events

  onClick?: (evt: React.MouseEvent<HTMLButtonElement>) => void
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      id,
      children,

      type = "submit",
      disabled = false,
      tabIndex = 0,
      autoFocus,

      iconLeftProps,
      iconRightProps,

      width = "auto",
      fullWidth = false,
      height = "32px",
      padding = "0 1rem",
      borderRadius,

      color = "text.light",
      background = "primary",
      fontSize = "1em",
      textTransform = "none",
      centerContent = false,

      onClick = () => void 0,
    },
    ref,
  ) => {
    const theme = useTheme()

    const overrideIconClick = useCallback(
      (position: "left" | "right", evt: React.MouseEvent<HTMLElement>) => {
        if (
          position === "left" &&
          !isUndefined(iconLeftProps) &&
          isFunction(iconLeftProps.onClick)
        )
          iconLeftProps.onClick(evt)
        else if (
          position === "right" &&
          !isUndefined(iconRightProps) &&
          isFunction(iconRightProps.onClick)
        )
          iconRightProps.onClick(evt)
      },
      [iconLeftProps, iconRightProps],
    )

    const parsedBorderRadius = borderRadius ?? theme.sizes.defaultBorderRadius
    const iconLeftBackground = iconLeftProps?.background ?? "urShadow"
    const iconRightBackground = iconRightProps?.background ?? "urShadow"

    return (
      <ButtonWrapper
        ref={ref}
        className={className}
        id={id}
        type={type}
        tabIndex={tabIndex}
        disabled={disabled}
        autoFocus={autoFocus}
        width={fullWidth ? "100%" : width}
        height={height}
        borderRadius={parsedBorderRadius}
        color={color}
        background={background}
        fontSize={fontSize}
        onClick={onClick}
      >
        {iconLeftProps && (
          <IconWrapper
            className="--button-icon-left"
            position="left"
            color={color}
            background={iconLeftBackground}
            height={height}
            buttonBackground={background}
            borderRadius={parsedBorderRadius}
            onClick={(e) => overrideIconClick("left", e)}
          >
            <Icon {...iconLeftProps} onClick={undefined} />
          </IconWrapper>
        )}

        <Content
          textTransform={textTransform}
          className="--button-content"
          padding={padding}
          center={centerContent}
        >
          {children}
        </Content>

        {iconRightProps && (
          <IconWrapper
            className="--button-icon-right"
            position="right"
            color={color}
            background={iconRightBackground}
            height={height}
            buttonBackground={background}
            borderRadius={parsedBorderRadius}
            onClick={(e) => overrideIconClick("right", e)}
          >
            <Icon {...iconRightProps} onClick={undefined} />
          </IconWrapper>
        )}
      </ButtonWrapper>
    )
  },
)
